// Layer A: invisible Unicode / homoglyph detection + cleaning.
// Faithful JS port of skills/remove-ai-marks/scripts/text_unicode.py

export const STRIP_CODEPOINTS = new Set([
  0x00ad, 0x034f, 0x061c, 0x115f, 0x1160, 0x17b4, 0x17b5, 0x180b, 0x180c,
  0x180d, 0x180e, 0x200b, 0x200c, 0x200d, 0x200e, 0x200f, 0x202a, 0x202b,
  0x202c, 0x202d, 0x202e, 0x2060, 0x2061, 0x2062, 0x2063, 0x2064, 0x2066,
  0x2067, 0x2068, 0x2069, 0x206a, 0x206b, 0x206c, 0x206d, 0x206e, 0x206f,
  0xfeff, 0xfe00, 0xfe01, 0xfe02, 0xfe03, 0xfe04, 0xfe05, 0xfe06, 0xfe07,
  0xfe08, 0xfe09, 0xfe0a, 0xfe0b, 0xfe0c, 0xfe0d, 0xfe0e, 0xfe0f, 0xfff9,
  0xfffa, 0xfffb,
]);

export const SPACE_HOMOGLYPHS = new Map([
  [0x00a0, " "], [0x1680, " "], [0x2000, " "], [0x2001, " "], [0x2002, " "],
  [0x2003, " "], [0x2004, " "], [0x2005, " "], [0x2006, " "], [0x2007, " "],
  [0x2008, " "], [0x2009, " "], [0x200a, " "], [0x202f, " "], [0x205f, " "],
  [0x3000, " "],
]);

const CYRILLIC = { 0x0410: "A", 0x0412: "B", 0x0415: "E", 0x041a: "K", 0x041c: "M", 0x041d: "H", 0x041e: "O", 0x0420: "P", 0x0421: "C", 0x0422: "T", 0x0425: "X", 0x0430: "a", 0x0435: "e", 0x043e: "o", 0x0440: "p", 0x0441: "c", 0x0443: "y", 0x0445: "x", 0x0456: "i" };

export const LATIN_CONFUSABLES = new Map(Object.entries(CYRILLIC).map(([k, v]) => [Number(k), v]));
// Fullwidth A-Z / a-z
for (let i = 0; i < 26; i++) {
  LATIN_CONFUSABLES.set(0xff21 + i, String.fromCharCode(65 + i));
  LATIN_CONFUSABLES.set(0xff41 + i, String.fromCharCode(97 + i));
}

const BIDI_CPS = new Set([0x061c, 0x200e, 0x200f, 0x202a, 0x202b, 0x202c, 0x202d, 0x202e, 0x2066, 0x2067, 0x2068, 0x2069]);
const ZW_FAMILY = new Set([0x200b, 0x200c, 0x200d, 0x2060, 0xfeff, 0x180e]);
const EMOJI_GLUE = new Set([0x200d, 0xfe0e, 0xfe0f]);
const SCRIPT_JOINERS = new Set([0x200c, 0x200d]);
const ORTHOGRAPHIC_CF = new Set([0x0600, 0x0601, 0x0602, 0x0603, 0x0604, 0x0605, 0x06dd, 0x070f, 0x08e2, 0x110bd, 0x110cd]);

const RE_CF = /\p{Cf}/u;
const RE_LM = /[\p{L}\p{M}]/u;

const isTagChar = (cp) => cp >= 0xe0001 && cp <= 0xe007f;
const isTagRange = (cp) => cp >= 0xe0020 && cp < 0xe0080;
const isVsSupplement = (cp) => cp >= 0xe0100 && cp < 0xe01f0;

function isStripCp(cp) {
  return STRIP_CODEPOINTS.has(cp) || isVsSupplement(cp) || isTagChar(cp);
}

function stripKind(cp) {
  if (isTagChar(cp)) return "tag_chars";
  if (isVsSupplement(cp) || (cp >= 0xfe00 && cp <= 0xfe0f) || (cp >= 0x180b && cp <= 0x180d)) return "variation_selector";
  if (BIDI_CPS.has(cp)) return "bidi";
  if (ZW_FAMILY.has(cp)) return "zwj_family";
  return "strip";
}

function isEmojiBase(cp) {
  if (cp >= 0x1f000 && cp <= 0x1faff) return true;
  if (cp >= 0x2600 && cp <= 0x27bf) return true;
  if (cp >= 0x2b00 && cp <= 0x2bff) return true;
  if ([0x00a9, 0x00ae, 0x2122, 0x3030, 0x303d, 0x3297, 0x3299].includes(cp)) return true;
  if (cp === 0x23 || cp === 0x2a || (cp >= 0x30 && cp <= 0x39)) return true;
  return false;
}

const isJoiningLetter = (cp) => cp > 0x7f && RE_LM.test(String.fromCodePoint(cp));
const isGlue = (cp) => EMOJI_GLUE.has(cp) || SCRIPT_JOINERS.has(cp) || isTagRange(cp);

function decide(ch, prevKept, { normalizeSpaces, treatConfusables, stripEmojiGlue }) {
  const cp = ch.codePointAt(0);
  if (EMOJI_GLUE.has(cp) && !stripEmojiGlue) {
    if (prevKept !== null && isEmojiBase(prevKept.codePointAt(0))) return ["keep", ch, null];
  }
  if (!stripEmojiGlue) {
    if (SCRIPT_JOINERS.has(cp) && prevKept !== null && isJoiningLetter(prevKept.codePointAt(0))) return ["keep", ch, null];
    if (isTagRange(cp) && prevKept !== null && isEmojiBase(prevKept.codePointAt(0))) return ["keep", ch, null];
    if (ORTHOGRAPHIC_CF.has(cp)) return ["keep", ch, null];
  }
  if (isStripCp(cp)) return ["strip", "", stripKind(cp)];
  if (normalizeSpaces && SPACE_HOMOGLYPHS.has(cp)) return ["replace", SPACE_HOMOGLYPHS.get(cp), "space"];
  if (treatConfusables && LATIN_CONFUSABLES.has(cp)) return ["replace", LATIN_CONFUSABLES.get(cp), "confusable"];
  if (RE_CF.test(ch) && !SPACE_HOMOGLYPHS.has(cp)) return ["strip", "", "other_cf"];
  return ["keep", ch, null];
}

const NAMES = {
  0x00ad: "SOFT HYPHEN", 0x034f: "COMBINING GRAPHEME JOINER", 0x061c: "ARABIC LETTER MARK",
  0x115f: "HANGUL CHOSEONG FILLER", 0x1160: "HANGUL JUNGSEONG FILLER",
  0x180e: "MONGOLIAN VOWEL SEPARATOR", 0x200b: "ZERO WIDTH SPACE",
  0x200c: "ZERO WIDTH NON-JOINER", 0x200d: "ZERO WIDTH JOINER",
  0x200e: "LEFT-TO-RIGHT MARK", 0x200f: "RIGHT-TO-LEFT MARK",
  0x202a: "LEFT-TO-RIGHT EMBEDDING", 0x202b: "RIGHT-TO-LEFT EMBEDDING",
  0x202c: "POP DIRECTIONAL FORMATTING", 0x202d: "LEFT-TO-RIGHT OVERRIDE",
  0x202e: "RIGHT-TO-LEFT OVERRIDE", 0x2060: "WORD JOINER",
  0x2061: "FUNCTION APPLICATION", 0x2062: "INVISIBLE TIMES",
  0x2063: "INVISIBLE SEPARATOR", 0x2064: "INVISIBLE PLUS",
  0x2066: "LEFT-TO-RIGHT ISOLATE", 0x2067: "RIGHT-TO-LEFT ISOLATE",
  0x2068: "FIRST STRONG ISOLATE", 0x2069: "POP DIRECTIONAL ISOLATE",
  0xfeff: "ZERO WIDTH NO-BREAK SPACE (BOM)",
  0x00a0: "NO-BREAK SPACE", 0x1680: "OGHAM SPACE MARK", 0x2000: "EN QUAD",
  0x2001: "EM QUAD", 0x2002: "EN SPACE", 0x2003: "EM SPACE",
  0x2004: "THREE-PER-EM SPACE", 0x2005: "FOUR-PER-EM SPACE",
  0x2006: "SIX-PER-EM SPACE", 0x2007: "FIGURE SPACE", 0x2008: "PUNCTUATION SPACE",
  0x2009: "THIN SPACE", 0x200a: "HAIR SPACE", 0x202f: "NARROW NO-BREAK SPACE",
  0x205f: "MEDIUM MATHEMATICAL SPACE", 0x3000: "IDEOGRAPHIC SPACE",
};

export function charLabel(cp) {
  let name = NAMES[cp];
  if (!name) {
    if (isTagChar(cp)) name = "TAG CHARACTER";
    else if (isVsSupplement(cp) || (cp >= 0xfe00 && cp <= 0xfe0f)) name = "VARIATION SELECTOR";
    else if (LATIN_CONFUSABLES.has(cp)) name = "CONFUSABLE LOOKALIKE";
    else name = "FORMAT CHARACTER";
  }
  return `U+${cp.toString(16).toUpperCase().padStart(4, "0")} ${name}`;
}

export const hitConfidence = (kind) => (kind === "space" ? "informational" : "probable");

export function inspectText(text, { aggressive = false, stripEmojiGlue = false } = {}) {
  const buckets = new Map();
  let prevKept = null;
  let i = 0;
  for (const ch of text) {
    const [action, outChar, kind] = decide(ch, prevKept, {
      normalizeSpaces: true, treatConfusables: aggressive, stripEmojiGlue,
    });
    if (kind === null) {
      if (!isGlue(ch.codePointAt(0))) prevKept = outChar;
      i += 1;
      continue;
    }
    const key = `${ch.codePointAt(0)}|${kind}`;
    if (!buckets.has(key)) buckets.set(key, { cp: ch.codePointAt(0), kind, offsets: [] });
    buckets.get(key).offsets.push(i);
    if (action === "replace") prevKept = outChar;
    i += 1;
  }
  const hits = [...buckets.values()]
    .sort((a, b) => b.offsets.length - a.offsets.length || a.cp - b.cp)
    .map((b) => ({
      codepoint: b.cp,
      code: `U+${b.cp.toString(16).toUpperCase().padStart(4, "0")}`,
      label: charLabel(b.cp),
      count: b.offsets.length,
      kind: b.kind,
      confidence: hitConfidence(b.kind),
      sampleOffsets: b.offsets.slice(0, 10),
    }));
  return {
    length: i,
    suspiciousTotal: hits.reduce((s, h) => s + h.count, 0),
    hits,
  };
}

export function cleanText(text, {
  nfkc = false, aggressiveHomoglyphs = false, normalizeSpaces = true, stripEmojiGlue = false,
} = {}) {
  const removed = new Map();
  const replaced = new Map();
  const out = [];
  let prevKept = null;
  for (const ch of text) {
    const [action, outChar] = decide(ch, prevKept, {
      normalizeSpaces, treatConfusables: aggressiveHomoglyphs, stripEmojiGlue,
    });
    if (action === "keep") {
      out.push(outChar);
      if (!isGlue(ch.codePointAt(0))) prevKept = outChar;
    } else if (action === "replace") {
      out.push(outChar);
      const k = charLabel(ch.codePointAt(0));
      replaced.set(k, (replaced.get(k) || 0) + 1);
      prevKept = outChar;
    } else {
      const k = charLabel(ch.codePointAt(0));
      removed.set(k, (removed.get(k) || 0) + 1);
    }
  }
  let result = out.join("");
  let nfkcChanged = 0;
  if (nfkc) {
    const before = result;
    result = result.normalize("NFKC");
    if (result !== before) nfkcChanged = Math.abs(before.length - result.length) || 1;
  }
  const sum = (m) => [...m.values()].reduce((a, b) => a + b, 0);
  return {
    text: result,
    stats: {
      inputLength: [...text].length,
      outputLength: [...result].length,
      removed: Object.fromEntries(removed),
      replaced: Object.fromEntries(replaced),
      removedCount: sum(removed),
      replacedCount: sum(replaced),
      nfkcChanged,
    },
  };
}
